import React, { useLayoutEffect, useRef, useState } from "react";
import { Box, Typography } from "@mui/material";
import { getS3Url } from "../../utils/imageUtils";
import {
  AssetKicker,
  AssetTitle,
  Card,
  CardLabel,
  ChannelAvatar,
  DataTable,
  LINKEDIN_ASSET_HEIGHT_PX,
  LINKEDIN_ASSET_WIDTH_PX,
  LinkedInAssetCanvas,
  MethodologyFooter,
  MUTED,
  CARD_BORDER,
  RADIUS,
  SANS,
} from "./linkedinAssetChrome";

export { LINKEDIN_ASSET_WIDTH_PX, LINKEDIN_ASSET_HEIGHT_PX };

export type LinkedInShareDayPoint = {
  at: string;
  viewers: number;
};

export type LinkedInShareDaySeries = {
  channelName: string;
  color: string | null;
  imageS3Key: string | null;
  points: LinkedInShareDayPoint[];
};

export type LinkedInShareDayRankingRow = {
  channelName: string;
  color: string | null;
  imageS3Key: string | null;
  streamId: string;
  title: string;
  programName: string | null;
  avgViewers: number;
  peakViewers: number;
  startedAt: string;
  endedAt: string;
  chatMessages: number | null;
  uniqueChatUsers: number | null;
};

export type LinkedInShareDayData = {
  day: string;
  dayLabel: string;
  category: string;
  start: string;
  end: string;
  series: LinkedInShareDaySeries[];
  ranking: LinkedInShareDayRankingRow[];
  /** All campaign-channel livestreams in the window (copy / post context). */
  programs?: LinkedInShareDayRankingRow[];
};

const FALLBACK_PALETTE = [
  "#7056EB",
  "#4ECDC4",
  "#F7B32B",
  "#FF6B6B",
  "#45B7D1",
  "#95E1A3",
  "#F06292",
  "#FFA726",
];

/** Match LinkedInLivestreamCompareAsset metrics table (≤4 rows, scale 1). */
const CHANNEL_AVATAR_PX = 44;
const METRICS_NAME_PX = 22;
const METRICS_CHANNEL_PX = 14;
const METRICS_ROW_PAD_Y = 14;

function LockMark({
  color = MUTED,
  size = 20,
}: {
  color?: string;
  size?: number;
}) {
  return (
    <Box
      component="svg"
      viewBox="0 0 16 16"
      aria-hidden
      fill="none"
      stroke={color}
      strokeWidth={1.5}
      strokeLinecap="round"
      sx={{
        width: size,
        height: size,
        display: "block",
        flexShrink: 0,
      }}
    >
      <rect x="3.2" y="7" width="9.6" height="6.4" rx="1.4" />
      <path d="M5.4 7V5.1a2.6 2.6 0 0 1 5.2 0V7" />
    </Box>
  );
}

const LockedChatMetric: React.FC<{ size?: number }> = ({ size = 20 }) => (
  <Box aria-hidden sx={{ display: "flex" }}>
    <LockMark size={size} />
  </Box>
);

function channelImageSrc(imageS3Key: string | null): string {
  return imageS3Key ? getS3Url(imageS3Key) : "";
}

function seriesColor(series: LinkedInShareDaySeries, index: number): string {
  if (series.color && /^#[0-9a-fA-F]{6}$/.test(series.color)) return series.color;
  return FALLBACK_PALETTE[index % FALLBACK_PALETTE.length]!;
}

function formatCompact(value: number): string {
  const rounded = Math.round(value);
  if (Math.abs(rounded) < 1000) return rounded.toLocaleString("es-CL");
  const compact = rounded / 1000;
  return `${compact.toFixed(1).replace(".", ",")}K`;
}

function formatChat(value: number | null | undefined): string {
  if (value == null) return "—";
  return formatCompact(value);
}

function formatHhMm(iso: string): string {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: "America/Santiago",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(new Date(iso));
  const hour = parts.find((part) => part.type === "hour")?.value ?? "00";
  const minute = parts.find((part) => part.type === "minute")?.value ?? "00";
  return `${hour}:${minute}`;
}

/** Chile local minutes from midnight for an ISO instant. */
function chileMinutesFromMidnight(iso: string): number {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: "America/Santiago",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(new Date(iso));
  const hour = Number(parts.find((part) => part.type === "hour")?.value ?? "0");
  const minute = Number(
    parts.find((part) => part.type === "minute")?.value ?? "0",
  );
  return hour * 60 + minute;
}

/** Logo placement: thickest share between 07:00 and 01:00 next day (Chile). */
const LOGO_THICKEST_START_MINUTES = 7 * 60;
const LOGO_THICKEST_END_MINUTES = 1 * 60; // 01:00 next calendar morning

function isInLogoThickestWindow(iso: string): boolean {
  const minutes = chileMinutesFromMidnight(iso);
  // Wraps midnight: 07:00→24:00 or 00:00→01:00.
  return (
    minutes >= LOGO_THICKEST_START_MINUTES ||
    minutes <= LOGO_THICKEST_END_MINUTES
  );
}

function programLabel(row: LinkedInShareDayRankingRow): string {
  return row.programName?.trim() || row.title.trim() || "Livestream";
}

function formatLiveRange(
  startedAt: string,
  endedAt: string,
  windowStart: string,
  windowEnd: string,
): string {
  const startMs = Math.max(
    new Date(startedAt).getTime(),
    new Date(windowStart).getTime(),
  );
  const endMs = Math.min(
    new Date(endedAt).getTime(),
    new Date(windowEnd).getTime(),
  );
  if (!Number.isFinite(startMs) || !Number.isFinite(endMs) || endMs <= startMs) {
    return "—";
  }
  return `${formatHhMm(new Date(startMs).toISOString())}–${formatHhMm(new Date(endMs).toISOString())}`;
}

/** Absolute channel minutes → stacked % rows (0–100) for SVG. */
function shareStacks(
  series: LinkedInShareDaySeries[],
): Array<{ at: string; shares: number[] }> {
  if (series.length === 0) return [];
  const length = series[0]!.points.length;
  const rows: Array<{ at: string; shares: number[] }> = [];
  for (let i = 0; i < length; i += 1) {
    const at = series[0]!.points[i]!.at;
    const values = series.map((row) => {
      const viewers = row.points[i]?.viewers;
      return typeof viewers === "number" && viewers > 0 ? viewers : 0;
    });
    const total = values.reduce((sum, value) => sum + value, 0);
    rows.push({
      at,
      shares:
        total > 0
          ? values.map((value) => (value / total) * 100)
          : values.map(() => 0),
    });
  }
  return rows;
}

function stackedAreaPath(
  stacks: Array<{ at: string; shares: number[] }>,
  seriesIndex: number,
  xFor: (index: number) => number,
  yFor: (pct: number) => number,
): string {
  if (stacks.length === 0) return "";
  const top: Array<{ x: number; y: number }> = [];
  const bottom: Array<{ x: number; y: number }> = [];
  stacks.forEach((row, index) => {
    let cumBelow = 0;
    for (let s = 0; s < seriesIndex; s += 1) cumBelow += row.shares[s] ?? 0;
    const value = row.shares[seriesIndex] ?? 0;
    const x = xFor(index);
    top.push({ x, y: yFor(cumBelow + value) });
    bottom.push({ x, y: yFor(cumBelow) });
  });
  let d = `M${top[0]!.x.toFixed(1)} ${top[0]!.y.toFixed(1)}`;
  for (let i = 1; i < top.length; i += 1) {
    d += ` L${top[i]!.x.toFixed(1)} ${top[i]!.y.toFixed(1)}`;
  }
  for (let i = bottom.length - 1; i >= 0; i -= 1) {
    d += ` L${bottom[i]!.x.toFixed(1)} ${bottom[i]!.y.toFixed(1)}`;
  }
  return `${d} Z`;
}

/** Horizontal band edge (top or bottom) for a stacked series. */
function stackedBandEdgePath(
  stacks: Array<{ at: string; shares: number[] }>,
  seriesIndex: number,
  which: "top" | "bottom",
  xFor: (index: number) => number,
  yFor: (pct: number) => number,
): string {
  if (stacks.length === 0) return "";
  const points = stacks.map((row, index) => {
    let cumBelow = 0;
    for (let s = 0; s < seriesIndex; s += 1) cumBelow += row.shares[s] ?? 0;
    const value = row.shares[seriesIndex] ?? 0;
    const pct = which === "top" ? cumBelow + value : cumBelow;
    return { x: xFor(index), y: yFor(pct) };
  });
  let d = `M${points[0]!.x.toFixed(1)} ${points[0]!.y.toFixed(1)}`;
  for (let i = 1; i < points.length; i += 1) {
    d += ` L${points[i]!.x.toFixed(1)} ${points[i]!.y.toFixed(1)}`;
  }
  return d;
}

const PROGRAM_LOGO_PX = 45;
const PROGRAM_LOGO_MIN_PX = 14;

function clampLogoInBand(
  bandTop: number,
  bandBottom: number,
  preferredSize: number,
): { y: number; size: number } {
  const bandHeight = Math.max(0, bandBottom - bandTop);
  const size = Math.max(
    PROGRAM_LOGO_MIN_PX,
    Math.min(preferredSize, bandHeight * 0.92),
  );
  const half = size / 2;
  const center = (bandTop + bandBottom) / 2;
  if (bandHeight <= size) {
    return { y: center, size: Math.max(PROGRAM_LOGO_MIN_PX, bandHeight * 0.92) };
  }
  return {
    y: Math.min(bandBottom - half, Math.max(bandTop + half, center)),
    size,
  };
}

const ShareChart: React.FC<{
  series: LinkedInShareDaySeries[];
  colors: string[];
  ranking: LinkedInShareDayRankingRow[];
  windowStart: string;
  windowEnd: string;
}> = ({ series, colors, ranking, windowStart, windowEnd }) => {
  const width = 928;
  const height = 420;
  const pad = { l: 56, r: 22, t: 12, b: 36 };
  // First series paints at the bottom of the stack — reverse so ranking #1 is on top.
  const stackSeries = [...series].reverse();
  const stackColors = [...colors].reverse();
  const stacks = shareStacks(stackSeries);
  const plotW = width - pad.l - pad.r;
  const plotH = height - pad.t - pad.b;
  const last = Math.max(stacks.length - 1, 1);
  const xFor = (index: number) => pad.l + (index / last) * plotW;
  const yFor = (pct: number) => pad.t + (1 - pct / 100) * plotH;
  const yTicks = [0, 25, 50, 75, 100];
  const xTickIndexes: number[] = [];
  if (stacks.length > 0) {
    const step = Math.max(1, Math.round(stacks.length / 6));
    for (let i = 0; i < stacks.length; i += step) xTickIndexes.push(i);
    const lastIndex = stacks.length - 1;
    if (xTickIndexes[xTickIndexes.length - 1] !== lastIndex) {
      xTickIndexes.push(lastIndex);
    }
  }

  const windowStartMs = new Date(windowStart).getTime();
  const windowEndMs = new Date(windowEnd).getTime();
  // During each best program, sit in that channel's band at its strongest share (07:00–01:00 Chile).
  const programMarkers = ranking
    .map((row) => {
      const seriesIndex = stackSeries.findIndex(
        (s) => s.channelName === row.channelName,
      );
      if (seriesIndex < 0 || stacks.length === 0) return null;
      const startMs = Math.max(
        new Date(row.startedAt).getTime(),
        windowStartMs,
      );
      const endMs = Math.min(new Date(row.endedAt).getTime(), windowEndMs);
      if (
        !Number.isFinite(startMs) ||
        !Number.isFinite(endMs) ||
        endMs <= startMs
      ) {
        return null;
      }

      let bestIndex = -1;
      let bestShare = -1;
      stacks.forEach((stack, sampleIndex) => {
        const atMs = new Date(stack.at).getTime();
        if (atMs < startMs || atMs > endMs) return;
        if (!isInLogoThickestWindow(stack.at)) return;
        const share = stack.shares[seriesIndex] ?? 0;
        if (share > bestShare) {
          bestShare = share;
          bestIndex = sampleIndex;
        }
      });
      if (bestIndex < 0 || bestShare <= 0) {
        stacks.forEach((stack, sampleIndex) => {
          if (!isInLogoThickestWindow(stack.at)) return;
          const share = stack.shares[seriesIndex] ?? 0;
          if (share > bestShare) {
            bestShare = share;
            bestIndex = sampleIndex;
          }
        });
      }
      if (bestIndex < 0 || bestShare <= 0) return null;

      const sample = stacks[bestIndex]!;
      let cumBelow = 0;
      for (let s = 0; s < seriesIndex; s += 1) cumBelow += sample.shares[s] ?? 0;
      const share = sample.shares[seriesIndex] ?? 0;
      if (share <= 0) return null;
      const bandTop = yFor(cumBelow + share);
      const bandBottom = yFor(cumBelow);
      const { y, size } = clampLogoInBand(bandTop, bandBottom, PROGRAM_LOGO_PX);
      const src = channelImageSrc(
        row.imageS3Key ?? stackSeries[seriesIndex]?.imageS3Key ?? null,
      );
      if (!src) return null;
      return {
        id: row.streamId,
        src,
        color: stackColors[seriesIndex]!,
        label: `${programLabel(row)} · ${row.channelName}`,
        x: xFor(bestIndex),
        y,
        size,
        bandTop,
        bandBottom,
      };
    })
    .filter((marker): marker is NonNullable<typeof marker> => marker != null);

  for (let pass = 0; pass < 4; pass += 1) {
    for (let i = 0; i < programMarkers.length; i += 1) {
      const marker = programMarkers[i]!;
      for (let j = 0; j < i; j += 1) {
        const other = programMarkers[j]!;
        const dx = marker.x - other.x;
        const dy = marker.y - other.y;
        const minDist = (marker.size + other.size) / 2;
        if (dx * dx + dy * dy < minDist * minDist) {
          marker.x += minDist * 0.9;
        }
      }
      marker.x = Math.min(
        width - marker.size / 2 - 4,
        Math.max(pad.l + marker.size / 2, marker.x),
      );
      const clamped = clampLogoInBand(
        marker.bandTop,
        marker.bandBottom,
        marker.size,
      );
      marker.y = clamped.y;
      marker.size = clamped.size;
    }
  }

  // HTML overlays (not SVG <image>) so PNG export can preload/paint S3 logos.
  const frameRef = useRef<HTMLDivElement | null>(null);
  const [frameWidth, setFrameWidth] = useState(0);
  useLayoutEffect(() => {
    const node = frameRef.current;
    if (!node) return;
    const update = () => setFrameWidth(node.offsetWidth);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);
  const scale = frameWidth > 0 ? frameWidth / width : 1;

  return (
    <Box sx={{ width: "100%", overflow: "visible" }}>
      <Box ref={frameRef} sx={{ position: "relative", overflow: "visible" }}>
        <svg
          width="100%"
          viewBox={`0 0 ${width} ${height}`}
          role="img"
          aria-label="Share de audiencia concurrente"
          style={{ display: "block", overflow: "visible" }}
        >
          <defs>
            <clipPath id="share-plot-clip">
              <rect x={pad.l} y={pad.t} width={plotW} height={plotH} />
            </clipPath>
          </defs>
          {yTicks.map((tick) => {
            const y = yFor(tick);
            return (
              <g key={tick}>
                <line
                  x1={pad.l}
                  x2={width - pad.r}
                  y1={y}
                  y2={y}
                  stroke={CARD_BORDER}
                  strokeWidth={1}
                />
                <text
                  x={pad.l - 10}
                  y={y + 4}
                  textAnchor="end"
                  fill={MUTED}
                  fontFamily="DM Sans, sans-serif"
                  fontSize={14}
                  fontWeight={600}
                >
                  {tick}%
                </text>
              </g>
            );
          })}
          {xTickIndexes.map((index) => (
            <text
              key={index}
              x={xFor(index)}
              y={height - 8}
              textAnchor="middle"
              fill={MUTED}
              fontFamily="DM Sans, sans-serif"
              fontSize={14}
              fontWeight={600}
            >
              {formatHhMm(stacks[index]!.at)}
            </text>
          ))}
          <g clipPath="url(#share-plot-clip)">
            {stackSeries.map((row, index) => (
              <path
                key={row.channelName}
                d={stackedAreaPath(stacks, index, xFor, yFor)}
                fill={stackColors[index]}
                fillOpacity={0.82}
              />
            ))}
            {stackSeries.map((row, index) => (
              <g key={`${row.channelName}-edges`}>
                <path
                  d={stackedBandEdgePath(stacks, index, "top", xFor, yFor)}
                  fill="none"
                  stroke={stackColors[index]}
                  strokeWidth={1}
                  strokeLinecap="butt"
                  strokeLinejoin="round"
                />
                <path
                  d={stackedBandEdgePath(stacks, index, "bottom", xFor, yFor)}
                  fill="none"
                  stroke={stackColors[index]}
                  strokeWidth={1}
                  strokeLinecap="butt"
                  strokeLinejoin="round"
                />
              </g>
            ))}
          </g>
        </svg>
        {programMarkers.map((marker) => {
          const markerPx = marker.size * scale;
          return (
            <Box
              key={marker.id}
              component="img"
              src={marker.src}
              alt={marker.label}
              title={marker.label}
              data-share-logo-border-color={marker.color}
              data-share-logo-border-width="3"
              sx={{
                position: "absolute",
                width: markerPx,
                height: markerPx,
                left: marker.x * scale - markerPx / 2,
                top: marker.y * scale - markerPx / 2,
                borderRadius: RADIUS,
                objectFit: "cover",
                overflow: "hidden",
                bgcolor: "#fff",
                border: `3px solid ${marker.color}`,
                boxSizing: "border-box",
                pointerEvents: "none",
                boxShadow: "0 1px 2px rgba(26, 25, 48, 0.18)",
              }}
            />
          );
        })}
      </Box>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "center",
          gap: "10px 16px",
          mt: "12px",
          px: "4px",
        }}
      >
        {series.map((row, index) => (
          <Box
            key={row.channelName}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              minWidth: 0,
            }}
          >
            <Box
              sx={{
                width: 12,
                height: 12,
                borderRadius: "3px",
                bgcolor: colors[index],
                flexShrink: 0,
              }}
            />
            <Typography
              sx={{
                m: 0,
                fontFamily: SANS,
                fontSize: 13,
                fontWeight: 600,
                color: MUTED,
                whiteSpace: "nowrap",
              }}
            >
              {row.channelName}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

/** Stack + legend follow ranking (promedio espectadores), not campaign channel order. */
function seriesInRankingOrder(
  series: LinkedInShareDaySeries[],
  ranking: LinkedInShareDayRankingRow[],
): LinkedInShareDaySeries[] {
  const byName = new Map(series.map((row) => [row.channelName, row]));
  const ordered: LinkedInShareDaySeries[] = [];
  for (const row of ranking) {
    const match = byName.get(row.channelName);
    if (!match) continue;
    ordered.push(match);
    byName.delete(row.channelName);
  }
  ordered.push(...Array.from(byName.values()));
  return ordered;
}

const LinkedInShareDayAsset: React.FC<{ data: LinkedInShareDayData }> = ({
  data,
}) => {
  const series = seriesInRankingOrder(data.series, data.ranking);
  const colors = series.map((row, index) => seriesColor(row, index));
  const dayTitle = data.dayLabel
    ? data.dayLabel.charAt(0).toUpperCase() + data.dayLabel.slice(1)
    : "Lunes 2 nov";

  return (
    <LinkedInAssetCanvas>
      <Box>
        <AssetKicker>{`${dayTitle.toUpperCase()} · TV EN YOUTUBE`}</AssetKicker>
        <AssetTitle>
          TV en YouTube Live:
          <br />
          ¿Quién lideró el share?
        </AssetTitle>
      </Box>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          gap: "18px",
          justifyContent: "center",
        }}
      >
        <Card sx={{ pt: "24px", pb: "12px", overflow: "visible" }}>
          <CardLabel>% del total de espectadores concurrentes</CardLabel>
          <ShareChart
            series={series}
            colors={colors}
            ranking={data.ranking}
            windowStart={data.start}
            windowEnd={data.end}
          />
        </Card>

        <Box>
          <Card sx={{ pr: "28px", overflow: "visible" }}>
            <CardLabel sx={{ mb: "16px" }}>Mejor livestream del día por canal</CardLabel>
            <DataTable
              columns={`${CHANNEL_AVATAR_PX}px minmax(0, 1.4fr) minmax(120px, 0.55fr) minmax(86px, 0.5fr) minmax(86px, 0.5fr) minmax(74px, 0.4fr) minmax(86px, 0.5fr)`}
              nameFontSize={METRICS_NAME_PX}
              metaFontSize={METRICS_CHANNEL_PX}
              nameMetaGap={2}
              rowPy={`${METRICS_ROW_PAD_Y}px`}
              avatarSize={CHANNEL_AVATAR_PX}
              clipMeta={false}
              headers={[
                { label: "Programa", align: "left", gridColumn: "1 / 3" },
                {
                  label: (
                    <>
                      Horario
                      <br />
                      en vivo
                    </>
                  ),
                  align: "right",
                  gridColumn: 3,
                },
                {
                  label: (
                    <>
                      Promedio
                      <br />
                      Espectadores
                    </>
                  ),
                  sorted: true,
                  align: "right",
                  gridColumn: 4,
                },
                {
                  label: (
                    <>
                      Peak
                      <br />
                      Espectadores
                    </>
                  ),
                  align: "right",
                  gridColumn: 5,
                },
                {
                  label: (
                    <>
                      Mensajes
                      <br />
                      en Chat
                    </>
                  ),
                  align: "right",
                  gridColumn: 6,
                },
                {
                  label: (
                    <>
                      Espectadores
                      <br />
                      en Chat
                    </>
                  ),
                  align: "right",
                  gridColumn: 7,
                },
              ]}
              rows={data.ranking.map((row, index) => {
                const accent =
                  row.color && /^#[0-9a-fA-F]{6}$/.test(row.color)
                    ? row.color
                    : FALLBACK_PALETTE[index % FALLBACK_PALETTE.length]!;
                return {
                  key: row.streamId,
                  avatar: (
                    <ChannelAvatar
                      src={channelImageSrc(row.imageS3Key)}
                      accentColor={accent}
                      size={CHANNEL_AVATAR_PX}
                    />
                  ),
                  name: programLabel(row),
                  meta: row.channelName,
                  cells: [
                    <Box
                      key="horario"
                      component="span"
                      sx={{ whiteSpace: "nowrap" }}
                    >
                      {formatLiveRange(
                        row.startedAt,
                        row.endedAt,
                        data.start,
                        data.end,
                      )}
                    </Box>,
                    formatCompact(row.avgViewers),
                    formatCompact(row.peakViewers),
                    formatChat(row.chatMessages),
                    <LockedChatMetric key="chat-lock" size={20} />,
                  ],
                };
              })}
            />
          </Card>
          <Box
            sx={{
              mt: "8px",
              width: "100%",
              px: "28px",
              py: 0,
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              gap: "8px",
              boxSizing: "border-box",
              fontFamily: SANS,
              fontWeight: 600,
              fontSize: 16,
              lineHeight: 1.2,
              color: MUTED,
            }}
          >
            <LockMark color={MUTED} size={16} />
            <Box component="span">Espectadores en chat disponibles en Podscope</Box>
          </Box>
        </Box>
      </Box>

      <MethodologyFooter compact>
        {" "}
        Ventana Chile 06:00–02:00 ({data.day}). Share = % del total de
        espectadores concurrentes TV en YouTube. Tabla: mejor livestream por
        canal según promedio de la ventana.
      </MethodologyFooter>
    </LinkedInAssetCanvas>
  );
};

const SHARE_DAY_SAMPLE_STEP_MS = 10 * 60 * 1000;

function sampleViewersAt(
  series: LinkedInShareDaySeries[],
  index: number,
): number[] {
  return series.map((row) => {
    const value = row.points[index]?.viewers;
    return typeof value === "number" && value > 0 ? Math.round(value) : 0;
  });
}

function inferSampleStepMs(stacks: Array<{ at: string }>): number {
  for (let i = 1; i < stacks.length; i += 1) {
    const delta =
      new Date(stacks[i]!.at).getTime() - new Date(stacks[i - 1]!.at).getTime();
    if (Number.isFinite(delta) && delta > 0) return delta;
  }
  return SHARE_DAY_SAMPLE_STEP_MS;
}

function sampleDurationsMs(
  stacks: Array<{ at: string }>,
  stepMs: number,
): number[] {
  return stacks.map((_, index) => {
    if (index < stacks.length - 1) {
      const delta =
        new Date(stacks[index + 1]!.at).getTime() -
        new Date(stacks[index]!.at).getTime();
      return Number.isFinite(delta) && delta > 0 ? delta : stepMs;
    }
    return stepMs;
  });
}

function formatMinutesHours(minutes: number): string {
  const rounded = Math.round(minutes);
  const hours = minutes / 60;
  return `${rounded} min (${hours.toFixed(1)} h)`;
}

function formatPct1(value: number): string {
  return `${value.toFixed(1)}%`;
}

function formatInt(value: number): string {
  return Math.round(value).toLocaleString("es-CL");
}

function programLiveAt(
  programs: LinkedInShareDayRankingRow[],
  channelName: string,
  atIso: string,
): LinkedInShareDayRankingRow | null {
  const atMs = new Date(atIso).getTime();
  if (!Number.isFinite(atMs)) return null;
  const matches = programs.filter((row) => {
    if (row.channelName !== channelName) return false;
    const start = new Date(row.startedAt).getTime();
    const end = new Date(row.endedAt).getTime();
    return Number.isFinite(start) && Number.isFinite(end) && start <= atMs && atMs <= end;
  });
  if (matches.length === 0) return null;
  matches.sort((a, b) => b.avgViewers - a.avgViewers);
  return matches[0] ?? null;
}

function contiguousRanges(
  flags: boolean[],
  ats: string[],
  stepMs: number,
): Array<{ start: string; end: string }> {
  const ranges: Array<{ start: string; end: string }> = [];
  let start = -1;
  for (let i = 0; i <= flags.length; i += 1) {
    const on = i < flags.length && flags[i];
    if (on && start < 0) start = i;
    if (!on && start >= 0) {
      const last = i - 1;
      const endMs = new Date(ats[last]!).getTime() + stepMs;
      ranges.push({
        start: ats[start]!,
        end: new Date(endMs).toISOString(),
      });
      start = -1;
    }
  }
  return ranges;
}

function formatChileRange(startIso: string, endIso: string): string {
  return `${formatHhMm(startIso)}–${formatHhMm(endIso)}`;
}

/** Clipboard text for writing the LinkedIn post from this asset. */
export function buildShareDayRawDataText(data: LinkedInShareDayData): string {
  const series = seriesInRankingOrder(data.series, data.ranking);
  const stacks = shareStacks(series);
  const channelNames = series.map((row) => row.channelName);
  const programs = data.programs?.length ? data.programs : data.ranking;
  const rankingIds = new Set(data.ranking.map((row) => row.streamId));
  const stepMs = inferSampleStepMs(stacks);
  const stepMinutes = stepMs / 60_000;
  const durationsMs = sampleDurationsMs(stacks, stepMs);

  const viewerMinutesByChannel = channelNames.map(() => 0);
  let totalViewerMinutes = 0;
  const totals: number[] = [];
  const leaderIndex: number[] = [];

  stacks.forEach((stack, index) => {
    const viewers = sampleViewersAt(series, index);
    const total = viewers.reduce((sum, value) => sum + value, 0);
    totals.push(total);
    const durationMin = durationsMs[index]! / 60_000;
    totalViewerMinutes += total * durationMin;
    viewers.forEach((value, seriesIndex) => {
      viewerMinutesByChannel[seriesIndex] =
        (viewerMinutesByChannel[seriesIndex] ?? 0) + value * durationMin;
    });
    if (total <= 0) {
      leaderIndex.push(-1);
      return;
    }
    let best = 0;
    for (let s = 1; s < viewers.length; s += 1) {
      if (viewers[s]! > viewers[best]!) best = s;
    }
    leaderIndex.push(best);
  });

  const weightedShare = viewerMinutesByChannel.map((vm) =>
    totalViewerMinutes > 0 ? (vm / totalViewerMinutes) * 100 : 0,
  );

  const unweightedShare = channelNames.map((_, seriesIndex) => {
    const shares = stacks
      .map((stack, index) =>
        totals[index]! > 0 ? (stack.shares[seriesIndex] ?? 0) : null,
      )
      .filter((share): share is number => share != null && share > 0);
    if (shares.length === 0) return 0;
    return shares.reduce((sum, share) => sum + share, 0) / shares.length;
  });

  const leadMinutes = channelNames.map((_, seriesIndex) =>
    leaderIndex.reduce(
      (sum, leader, index) =>
        leader === seriesIndex ? sum + durationsMs[index]! / 60_000 : sum,
      0,
    ),
  );

  const leadingWindows = channelNames.map((name, seriesIndex) => {
    const flags = leaderIndex.map((leader) => leader === seriesIndex);
    return contiguousRanges(
      flags,
      stacks.map((stack) => stack.at),
      stepMs,
    ).map((range) => `${name} ${formatChileRange(range.start, range.end)}`);
  });

  const peakShare = channelNames.map((name, seriesIndex) => {
    let bestShare = -1;
    let bestIndex = -1;
    stacks.forEach((stack, index) => {
      if (totals[index]! <= 0) return;
      const share = stack.shares[seriesIndex] ?? 0;
      if (share > bestShare) {
        bestShare = share;
        bestIndex = index;
      }
    });
    if (bestIndex < 0) {
      return {
        name,
        share: 0,
        at: null as string | null,
        program: null as string | null,
      };
    }
    const at = stacks[bestIndex]!.at;
    const live = programLiveAt(programs, name, at);
    return {
      name,
      share: bestShare,
      at,
      program: live ? programLabel(live) : null,
    };
  });

  const samplesPerHour = Math.max(1, Math.round(60 / stepMinutes));
  const strongestHour = channelNames.map((name, seriesIndex) => {
    let bestAvg = -1;
    let bestStart = -1;
    for (let start = 0; start + samplesPerHour <= stacks.length; start += 1) {
      let sum = 0;
      let count = 0;
      for (let i = start; i < start + samplesPerHour; i += 1) {
        if (totals[i]! <= 0) continue;
        sum += stacks[i]!.shares[seriesIndex] ?? 0;
        count += 1;
      }
      if (count === 0) continue;
      const avg = sum / count;
      if (avg > bestAvg) {
        bestAvg = avg;
        bestStart = start;
      }
    }
    if (bestStart < 0) {
      return {
        name,
        avg: 0,
        start: null as string | null,
        end: null as string | null,
        program: null as string | null,
      };
    }
    const startAt = stacks[bestStart]!.at;
    const endAt = new Date(
      new Date(stacks[bestStart + samplesPerHour - 1]!.at).getTime() + stepMs,
    ).toISOString();
    const midAt = new Date(
      (new Date(startAt).getTime() + new Date(endAt).getTime()) / 2,
    ).toISOString();
    const live = programLiveAt(programs, name, midAt);
    return {
      name,
      avg: bestAvg,
      start: startAt,
      end: endAt,
      program: live ? programLabel(live) : null,
    };
  });

  const noLiveGaps = channelNames.map((name, seriesIndex) => {
    const flags = stacks.map((_, index) => sampleViewersAt(series, index)[seriesIndex]! <= 0);
    return contiguousRanges(
      flags,
      stacks.map((stack) => stack.at),
      stepMs,
    ).map((range) => `${formatChileRange(range.start, range.end)}`);
  });

  let peakTotal = -1;
  let peakTotalAt: string | null = null;
  let lowTotal = Number.POSITIVE_INFINITY;
  let lowTotalAt: string | null = null;
  totals.forEach((total, index) => {
    if (total > peakTotal) {
      peakTotal = total;
      peakTotalAt = stacks[index]!.at;
    }
    if (total < lowTotal) {
      lowTotal = total;
      lowTotalAt = stacks[index]!.at;
    }
  });
  if (!Number.isFinite(lowTotal)) lowTotal = 0;

  const lines: string[] = [
    "Raw data for LinkedIn asset: share day (TV en YouTube)",
    "Use these numbers to write LinkedIn post copy. Times are Chile (America/Santiago). Samples every 10 minutes unless noted.",
    "",
    "## Contexto",
    `Día: ${data.dayLabel} (${data.day})`,
    `Categoría: ${data.category}`,
    `Ventana Chile: 06:00–02:00`,
    `UTC start: ${data.start}`,
    `UTC end: ${data.end}`,
    `Canales: ${channelNames.join(", ")}`,
    `Intervalo de muestra: ${stepMinutes} min (${stepMs} ms)`,
    `Muestras: ${stacks.length}`,
    `Título del asset: TV en YouTube Live: ¿Quién lideró el share?`,
    "",
    "## Resumen (para el post)",
    "",
    "### Share ponderado (viewer-minutes / total viewer-minutes)",
    "Weighted by sample duration × concurrent viewers. Prefer this over unweighted mean for “share del día”.",
  ];

  channelNames
    .map((name, index) => ({
      name,
      weighted: weightedShare[index]!,
      viewerMinutes: viewerMinutesByChannel[index]!,
    }))
    .sort((a, b) => b.weighted - a.weighted)
    .forEach((row, index) => {
      lines.push(
        `${index + 1}. ${row.name}: ${formatPct1(row.weighted)} · ${formatInt(row.viewerMinutes)} viewer-min`,
      );
    });
  lines.push(`Total viewer-minutes (todos los canales): ${formatInt(totalViewerMinutes)}`);

  lines.push("");
  lines.push(
    "### Share promedio no ponderado (media aritmética de % por muestra con total > 0)",
  );
  channelNames
    .map((name, index) => ({ name, avg: unweightedShare[index]! }))
    .sort((a, b) => b.avg - a.avg)
    .forEach((row, index) => {
      lines.push(`${index + 1}. ${row.name}: ${formatPct1(row.avg)}`);
    });

  lines.push("");
  lines.push(
    `### Tiempo liderando el share (intervalo de muestra = ${stepMinutes} min)`,
  );
  channelNames
    .map((name, index) => ({ name, minutes: leadMinutes[index]! }))
    .sort((a, b) => b.minutes - a.minutes)
    .forEach((row, index) => {
      lines.push(`${index + 1}. ${row.name}: ${formatMinutesHours(row.minutes)}`);
    });

  lines.push("");
  lines.push("### Ventanas liderando el share (rangos contiguos)");
  leadingWindows.forEach((windows, index) => {
    const name = channelNames[index]!;
    if (windows.length === 0) {
      lines.push(`${name}: (ninguna)`);
      return;
    }
    lines.push(`${name}: ${windows.join("; ")}`);
  });

  lines.push("");
  lines.push("### Peak share por canal");
  peakShare
    .slice()
    .sort((a, b) => b.share - a.share)
    .forEach((row) => {
      const when = row.at ? formatHhMm(row.at) : "—";
      const program = row.program ?? "(sin programa detectado)";
      lines.push(
        `${row.name}: ${formatPct1(row.share)} @ ${when} · programa: ${program}`,
      );
    });

  lines.push("");
  lines.push(
    `### Ventana más fuerte por canal (bloque ~${samplesPerHour * stepMinutes} min con mayor share promedio)`,
  );
  strongestHour
    .slice()
    .sort((a, b) => b.avg - a.avg)
    .forEach((row) => {
      if (!row.start || !row.end) {
        lines.push(`${row.name}: (sin datos)`);
        return;
      }
      lines.push(
        `${row.name}: ${formatPct1(row.avg)} · ${formatChileRange(row.start, row.end)} · programa: ${row.program ?? "(sin programa detectado)"}`,
      );
    });

  lines.push("");
  lines.push(
    "### Gaps sin live / 0 viewers (pueden inflar share de otros canales)",
  );
  noLiveGaps.forEach((gaps, index) => {
    const name = channelNames[index]!;
    if (gaps.length === 0) {
      lines.push(`${name}: (ninguno)`);
      return;
    }
    lines.push(`${name}: ${gaps.join("; ")}`);
  });

  lines.push("");
  lines.push("### Total concurrente (suma de canales)");
  lines.push(
    `Peak: ${formatInt(peakTotal)} @ ${peakTotalAt ? formatHhMm(peakTotalAt) : "—"}`,
  );
  lines.push(
    `Low: ${formatInt(lowTotal)} @ ${lowTotalAt ? formatHhMm(lowTotalAt) : "—"}`,
  );

  lines.push("");
  lines.push(
    "## Promedio espectadores del día (columna Promedio del asset — mejor livestream)",
  );
  data.ranking.forEach((row, index) => {
    lines.push(
      `${index + 1}. ${row.channelName} · ${programLabel(row)}: ${formatInt(row.avgViewers)} prom. esp. (peak ${formatInt(row.peakViewers)})`,
    );
  });

  lines.push("");
  lines.push("## Mejor livestream del día por canal (ranking del asset)");
  data.ranking.forEach((row, index) => {
    const horario = formatLiveRange(
      row.startedAt,
      row.endedAt,
      data.start,
      data.end,
    );
    lines.push(
      [
        `${index + 1}. ${programLabel(row)} · ${row.channelName}`,
        `   Horario en vivo (ventana): ${horario}`,
        `   Promedio espectadores: ${Math.round(row.avgViewers)}`,
        `   Peak espectadores: ${Math.round(row.peakViewers)}`,
        `   Mensajes en chat: ${row.chatMessages == null ? "—" : Math.round(row.chatMessages)}`,
        `   Espectadores en chat: locked in asset (Podscope); raw=${row.uniqueChatUsers == null ? "—" : Math.round(row.uniqueChatUsers)}`,
        `   Stream id: ${row.streamId}`,
        `   Título stream: ${row.title}`,
      ].join("\n"),
    );
  });

  lines.push("");
  lines.push(
    "## Programas por canal (todos los livestreams de la ventana)",
  );
  let currentChannel = "";
  programs.forEach((row) => {
    if (row.channelName !== currentChannel) {
      currentChannel = row.channelName;
      lines.push("");
      lines.push(`### ${currentChannel}`);
    }
    const horario = formatLiveRange(
      row.startedAt,
      row.endedAt,
      data.start,
      data.end,
    );
    const isBest = rankingIds.has(row.streamId);
    lines.push(
      [
        `- ${programLabel(row)}${isBest ? " ★ mejor del canal en el asset" : ""}`,
        `  Título: ${row.title}`,
        `  Horario (ventana): ${horario}`,
        `  Started: ${row.startedAt} · Ended: ${row.endedAt}`,
        `  Prom. esp.: ${formatInt(row.avgViewers)} · Peak: ${formatInt(row.peakViewers)}`,
        `  Chat mensajes: ${row.chatMessages == null ? "—" : formatInt(row.chatMessages)} · Chat users: ${row.uniqueChatUsers == null ? "—" : formatInt(row.uniqueChatUsers)}`,
        `  Stream id: ${row.streamId}`,
      ].join("\n"),
    );
  });

  lines.push("");
  lines.push(
    `## Tabla por muestra (${stepMinutes} min, Chile) — después del resumen`,
  );
  lines.push(
    ["hora_CL", "total_viewers", ...channelNames.flatMap((name) => [`${name}_viewers`, `${name}_share_%`])].join("\t"),
  );
  stacks.forEach((stack, index) => {
    const viewers = sampleViewersAt(series, index);
    const total = totals[index]!;
    lines.push(
      [
        formatHhMm(stack.at),
        String(total),
        ...viewers.flatMap((value, seriesIndex) => [
          String(value),
          (stack.shares[seriesIndex] ?? 0).toFixed(1),
        ]),
      ].join("\t"),
    );
  });

  const programJson = (row: LinkedInShareDayRankingRow) => ({
    channelName: row.channelName,
    programName: programLabel(row),
    title: row.title,
    avgViewers: Math.round(row.avgViewers),
    peakViewers: Math.round(row.peakViewers),
    chatMessages: row.chatMessages,
    uniqueChatUsers: row.uniqueChatUsers,
    startedAt: row.startedAt,
    endedAt: row.endedAt,
    streamId: row.streamId,
    bestInAsset: rankingIds.has(row.streamId),
  });

  lines.push("");
  lines.push("## JSON ranking");
  lines.push(JSON.stringify(data.ranking.map(programJson), null, 2));
  lines.push("");
  lines.push("## JSON programs by channel");
  lines.push(JSON.stringify(programs.map(programJson), null, 2));
  lines.push("");
  lines.push("## JSON resumen share");
  lines.push(
    JSON.stringify(
      {
        sampleStepMinutes: stepMinutes,
        totalViewerMinutes: Math.round(totalViewerMinutes),
        channels: channelNames.map((name, index) => ({
          channelName: name,
          weightedSharePct: Number(weightedShare[index]!.toFixed(1)),
          unweightedSharePct: Number(unweightedShare[index]!.toFixed(1)),
          viewerMinutes: Math.round(viewerMinutesByChannel[index]!),
          leadMinutes: Math.round(leadMinutes[index]!),
          leadingWindows: leadingWindows[index],
          peakShare: peakShare[index],
          strongestHour: strongestHour[index],
          noLiveGaps: noLiveGaps[index],
        })),
        totalConcurrent: {
          peak: peakTotal,
          peakAt: peakTotalAt,
          low: lowTotal,
          lowAt: lowTotalAt,
        },
      },
      null,
      2,
    ),
  );

  return lines.join("\n");
}

export default LinkedInShareDayAsset;
